import get from 'lodash-es/get'
import isArray from 'lodash-es/isArray'
import isBoolean from 'lodash-es/isBoolean'
import isEmpty from 'lodash-es/isEmpty'
import isEqual from 'lodash-es/isEqual'
import isObject from 'lodash-es/isObject'
import omit from 'lodash-es/omit'
import omitBy from 'lodash-es/omitBy'

import { EMPTY_LIST, EMPTY_OBJECT } from '../constants/common'
import {
  FORMIK_EMPTY_VALUE,
  FORMIK_SELECT_EMPTY_VALUE,
} from '../constants/validation'

import { getItemsIds } from './common'

export function getActiveItems(values, path) {
  const value = get(values, path)

  if (value) {
    return [value]
  }

  return EMPTY_LIST
}

export function checkOnRequired(values, field) {
  return isObject(values[field])
    ? !(get(values, [field, 'value']) || get(values, [field, 'id']))
    : !values[field]
}

export function clearEmptyFields(params) {
  return omitBy(params, item => {
    if (isObject(item) && isEmpty(item)) {
      return true
    } else if (isArray(item) && item.length < 1) {
      return true
    }

    if (isBoolean(item)) {
      return false
    }

    return !item
  })
}

export function getCleanValues(values) {
  return Object.entries(values).reduce((cleanValues, [key, value]) => {
    if (isArray(value)) {
      if (value.length > 0) {
        cleanValues[key] = value.map(getCleanValues)
      }
    } else if (
      value !== FORMIK_EMPTY_VALUE &&
      value !== FORMIK_SELECT_EMPTY_VALUE
    ) {
      cleanValues[key] = value
    }

    return cleanValues
  }, {})
}

export function checkOnDirty(values, initial, toOmit, toOmitInitial) {
  if (!toOmit && !isEqual(values, initial)) {
    return true
  }

  if (!isEqual(omit(initial, [toOmit]), omit(values, [toOmit]))) {
    return true
  }

  let isDirty = false

  if (toOmit && values[toOmit].length > 0) {
    values[toOmit].map(ins => {
      if (checkOnDirty(ins, toOmitInitial)) {
        isDirty = true
      }
    })
  }

  return isDirty
}

export function objectValidation(value, key = 'id') {
  if (!value) {
    return true
  }

  return value && value[key]
}

export function objectNumIdValidation(value, key = 'id') {
  return Number.isSafeInteger(value?.[key])
}

export function getNextMultiValue(
  value,
  newValue,
  getValue = item => item?.value
) {
  if (isArray(newValue)) {
    const ids = newValue.map(getValue)

    return [...value.filter(i => !ids.includes(getValue(i))), ...newValue]
  }

  if (newValue && !getValue(newValue) && (!value[0] || !getValue(value[0]))) {
    return [newValue]
  }

  const included = value.filter(i => getValue(i) === getValue(newValue))

  if (included.length > 0) {
    return value.filter(i => getValue(i) !== getValue(newValue))
  } else {
    return [...value.filter(i => getValue(i)), newValue]
  }
}

export function isFormArrayFieldsEqual({ initialValues, name, values }) {
  const isAllEqual = isEqual(omit(initialValues, name), omit(values, name))

  const isEqualItems = isEqual(
    initialValues[name].map(item => item.id || item.value).sort(),
    values[name].map(item => item.id || item.value).sort()
  )

  return isAllEqual && isEqualItems
}

export function isFormFieldsEqual({ initialValues, values }) {
  return isEqual(initialValues, values)
}

export function isFormFieldsWithFilesEqual({
  files,
  initialFiles,
  initialValues,
  values,
}) {
  const isFormikFieldsEqual = isFormFieldsEqual({ initialValues, values })

  const initialFileIds = getItemsIds(initialFiles || EMPTY_LIST)
  const currentFileIds = getItemsIds(files || EMPTY_LIST)

  const isFilesEqual = isEqual(initialFileIds, currentFileIds)

  return isFormikFieldsEqual && isFilesEqual
}

/**
 * The {@link isDirtyForm} function checks form dirty state
 *
 * @param {{dirty: boolean, files: *[] | undefined, values: *}} [params]
 */
export function isDirtyForm({ dirty, files = EMPTY_LIST } = EMPTY_OBJECT) {
  return dirty || files.length > 0
}
